(function () {
  "use strict";

  const MESSAGE_PREFIX = "gs-snack-swipe:";
  const SWIPE_CONTROL_PARAMS = [
    "swipe",
    "swipe_product",
    "swipe_view",
    "swipe_filter",
    "swipe_country",
    "swipe_mission",
    "swipe_type",
    "swipe_snack_type",
    "swipe_intro",
    "swipe_debug",
    "swipe_desktop",
    "showIntro"
  ];

  const LAUNCHER_ID = "gs-swipe-mode-overlay";
  const LAUNCH_TRIGGER_SELECTOR = "[data-gs-open-swipe-mode], a[href*='swipe=1']";
  const SNACK_SWIPE_ANALYTICS_VERSION = "v7_order_product_bridge_20260621";
  const config = window.GalacticSnackSwipeLauncher || {};
  const APP_PATH = config.appPath || "/pages/swipe-test";
  const LOGO_ASSET = config.logoUrl || "";
  const APP_PATHS = [
    APP_PATH,
    ...(Array.isArray(config.fallbackAppPaths) ? config.fallbackAppPaths : [])
  ]
    .filter((path, index, paths) => path && paths.indexOf(path) === index);

  let overlay;
  let mount;
  let lastActiveElement = null;
  let previousBodyOverflow = "";
  let previousDocumentOverflow = "";
  let isOpen = false;
  let activeDebug = false;
  let isolatedPageChildren = [];
  let entryImpressionTracked = false;
  let entryIntersectionObserver = null;
  let entryMutationObserver = null;

  const FOCUSABLE_SELECTOR = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[tabindex]:not([tabindex='-1'])",
    "[contenteditable='true']"
  ].join(",");

  const APP_PANEL_SELECTOR = [
    "[data-gs-details]:not([hidden])",
    "[data-gs-filter-overlay]:not([hidden])",
    "[data-gs-list]:not([hidden])",
    "[data-gs-intro]:not([hidden])",
    "[data-gs-gesture-demo]:not([hidden])"
  ].join(",");

  const sameOriginUrl = (value) => {
    try {
      return new URL(value, window.location.origin);
    } catch (error) {
      return new URL(window.location.href);
    }
  };

  const productHandleFromPath = (url) => {
    const match = url.pathname.match(/^\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : "";
  };

  const firstParam = (params, names) => {
    for (const name of names) {
      const value = params.get(name);
      if (value) return value;
    }
    return "";
  };

  const appendIfPresent = (target, name, value) => {
    if (value) target.set(name, value);
  };

  const truthyParam = (value) => ["1", "true", "yes", "on"].includes(String(value || "").toLowerCase());

  const debugEnabledFor = (sourceUrl) => {
    const source = sameOriginUrl(sourceUrl || window.location.href);
    return config.debug === true || truthyParam(source.searchParams.get("swipe_debug"));
  };

  const mobileOnlyLaunchEnabled = () => config.mobileOnly !== false;

  const mobileLaunchViewport = () => (
    window.matchMedia?.("(max-width: 767px)")?.matches
    || window.matchMedia?.("(pointer: coarse)")?.matches
  );

  const desktopLaunchOverrideFor = (sourceUrl) => {
    const source = sameOriginUrl(sourceUrl || window.location.href);
    return config.allowDesktop === true
      || truthyParam(source.searchParams.get("swipe_debug"))
      || truthyParam(source.searchParams.get("swipe_desktop"));
  };

  const canLaunchSwipeMode = (sourceUrl) => (
    !mobileOnlyLaunchEnabled()
    || mobileLaunchViewport()
    || desktopLaunchOverrideFor(sourceUrl)
  );

  const debugLog = (eventName, payload = {}) => {
    if (!activeDebug) return;
    console.info(`[Snack Swipe Overlay] ${eventName}`, payload);
  };

  const deviceType = () => {
    const width = window.innerWidth || document.documentElement.clientWidth || 0;
    if (width < 768) return "mobile";
    if (width < 1100) return "tablet";
    return "desktop";
  };

  const ANALYTICS_NEUTRAL_PARAMS = {
    product_id: "not_applicable",
    variant_id: "not_applicable",
    product_handle: "not_applicable",
    product_title: "not_applicable",
    product_position: 0,
    product_price_bucket: "unknown",
    available: false,
    availability_status: "not_applicable",
    product_availability_status: "not_applicable",
    country: "not_applicable",
    product_country: "not_applicable",
    product_type: "not_applicable",
    snack_type: "not_applicable",
    flavor_profiles: "none",
    product_flavor_profiles: "none",
    missions: "none",
    product_missions: "none",
    card_badges: "none",
    product_badges: "none",
    bundle_flag: false,
    bundle_app: "none",
    review_rating: 0,
    review_count: 0,
    has_image: false,
    image_source: "not_applicable",
    selected_flavors: "none",
    selected_countries: "none",
    selected_snack_types: "none",
    selected_missions: "none",
    selected_filter_values: "none",
    selected_group_count: 0,
    selected_value_count: 0,
    filter_combo_key: "none",
    filter_logic: "none",
    swipe_action: "none",
    input_type: "none",
    source: "none",
    result: "none",
    cart_action: "none",
    cart_source: "none",
    cart_result: "none",
    prior_swipe_action: "none",
    item_count: 0,
    quantity: 0,
    value: 0,
    estimated_value: 0,
    items_added_count: 0,
    rebuy_drawer_opened: false
  };

  const isValidLaunchTrigger = (trigger) => {
    if (!trigger) return false;
    if (trigger.hasAttribute("data-gs-open-swipe-mode")) return true;
    if (!trigger.matches("a")) return false;
    const url = sameOriginUrl(trigger.getAttribute("href") || window.location.href);
    return url.origin === window.location.origin && url.searchParams.get("swipe") === "1";
  };

  const isVisibleLaunchTrigger = (trigger) => {
    if (!isValidLaunchTrigger(trigger)) return false;
    const rect = trigger.getBoundingClientRect();
    const style = window.getComputedStyle(trigger);
    return rect.width > 0
      && rect.height > 0
      && rect.bottom > 0
      && rect.right > 0
      && rect.top < (window.innerHeight || document.documentElement.clientHeight)
      && rect.left < (window.innerWidth || document.documentElement.clientWidth)
      && style.visibility !== "hidden"
      && style.display !== "none"
      && style.pointerEvents !== "none";
  };

  const entryImpressionKey = () => {
    const current = sameOriginUrl(window.location.href);
    return `gs_snack_swipe_entry_impression:${current.pathname}`;
  };

  const stopEntryImpressionWatch = () => {
    entryIntersectionObserver?.disconnect();
    entryMutationObserver?.disconnect();
    entryIntersectionObserver = null;
    entryMutationObserver = null;
  };

  const pushLauncherAnalytics = (eventName, params = {}) => {
    const payload = {
      source_feature: "snack_swipe",
      snack_swipe_original_event: eventName,
      snack_swipe_event_name: eventName,
      snack_swipe_version: SNACK_SWIPE_ANALYTICS_VERSION,
      event_id: `swipe_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      event_timestamp: new Date().toISOString(),
      ...ANALYTICS_NEUTRAL_PARAMS,
      ...params
    };
    debugLog(`analytics:${eventName}`, payload);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ...payload
    });
  };

  const trackEntryImpression = (trigger) => {
    if (entryImpressionTracked || !isVisibleLaunchTrigger(trigger)) return;

    const key = entryImpressionKey();
    try {
      if (window.sessionStorage?.getItem(key)) {
        entryImpressionTracked = true;
        stopEntryImpressionWatch();
        return;
      }
      window.sessionStorage?.setItem(key, "1");
    } catch (_error) {
      // Some browsers block storage; still send one in-memory impression.
    }

    const current = sameOriginUrl(window.location.href);
    const triggerUrl = sameOriginUrl(trigger.getAttribute("href") || current.href);
    entryImpressionTracked = true;
    stopEntryImpressionWatch();
    pushLauncherAnalytics("snack_swipe_entry_impression", {
      swipe_surface: "entry_button",
      swipe_entrypoint: productHandleFromPath(current) ? "product_page" : "storefront",
      entry_trigger_type: trigger.hasAttribute("data-gs-open-swipe-mode") ? "button" : "link",
      entry_trigger_pathname: triggerUrl.pathname,
      page_pathname: current.pathname,
      device_type: deviceType()
    });
  };

  const watchEntryImpressions = () => {
    if (entryImpressionTracked || sameOriginUrl(window.location.href).searchParams.get("swipe") === "1") return;

    const triggers = Array.from(document.querySelectorAll(LAUNCH_TRIGGER_SELECTOR))
      .filter(isValidLaunchTrigger);
    const visibleTrigger = triggers.find(isVisibleLaunchTrigger);
    if (visibleTrigger) {
      trackEntryImpression(visibleTrigger);
      return;
    }

    if ("IntersectionObserver" in window && triggers.length) {
      entryIntersectionObserver?.disconnect();
      entryIntersectionObserver = new IntersectionObserver((entries) => {
        const visibleEntry = entries.find((entry) => entry.isIntersecting);
        if (visibleEntry) trackEntryImpression(visibleEntry.target);
      }, { threshold: 0.25 });
      triggers.forEach((trigger) => entryIntersectionObserver.observe(trigger));
    }

    if (!entryMutationObserver) {
      entryMutationObserver = new MutationObserver(() => watchEntryImpressions());
      entryMutationObserver.observe(document.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["href", "style", "class", "hidden", "aria-hidden"]
      });
    }
  };

  const elementDescriptor = (element) => [
    element?.id || "",
    typeof element?.className === "string" ? element.className : "",
    element?.getAttribute?.("data-rebuy-component") || "",
    element?.getAttribute?.("data-smartcart") || "",
    element?.getAttribute?.("data-smart-cart") || ""
  ].join(" ").toLowerCase();

  const isCartLayerElement = (element) => {
    const descriptor = elementDescriptor(element);
    return descriptor.includes("rebuy")
      || descriptor.includes("smartcart")
      || descriptor.includes("smart-cart")
      || descriptor.includes("smart_cart");
  };

  const isInsideCartLayer = (element) => {
    let current = element && element.nodeType === 1 ? element : element?.parentElement;
    while (current && current !== document.body) {
      if (isCartLayerElement(current)) return true;
      current = current.parentElement;
    }
    return false;
  };

  const isElementVisible = (element) => {
    if (!element || element.hidden) return false;
    const style = window.getComputedStyle(element);
    return style.display !== "none" && style.visibility !== "hidden" && element.getClientRects().length > 0;
  };

  const cartLayerIsActive = () => Array.from(document.body.children)
    .some((child) => child !== overlay && isCartLayerElement(child) && isElementVisible(child));

  const focusableIn = (container) => {
    if (!container) return [];
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter((element) => !element.disabled && element.getAttribute("aria-hidden") !== "true" && isElementVisible(element));
  };

  const focusSafely = (element) => {
    if (!element || typeof element.focus !== "function") return false;
    element.focus({ preventScroll: true });
    return true;
  };

  const appHasOpenPanel = () => Boolean(overlay?.querySelector(APP_PANEL_SELECTOR));

  const focusInitialOverlayTarget = () => {
    if (!overlay) return;
    const target = overlay.querySelector("[data-gs-exit-portal]")
      || overlay.querySelector("[data-gs-card]")
      || overlay.querySelector(".gs-swipe-mode-overlay__close");
    focusSafely(target);
  };

  const trapFocusInOverlay = (event) => {
    if (!overlay || event.key !== "Tab" || cartLayerIsActive()) return;
    const activeElement = document.activeElement;
    if (isInsideCartLayer(activeElement)) return;

    const focusable = focusableIn(overlay);
    if (!focusable.length) {
      event.preventDefault();
      focusSafely(overlay.querySelector(".gs-swipe-mode-overlay__frame"));
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && activeElement === first) {
      event.preventDefault();
      focusSafely(last);
    } else if (!event.shiftKey && activeElement === last) {
      event.preventDefault();
      focusSafely(first);
    } else if (!overlay.contains(activeElement)) {
      event.preventDefault();
      focusSafely(first);
    }
  };

  const handleOverlayKeydown = (event) => {
    if (!isOpen || !overlay || cartLayerIsActive()) return;
    if (event.key === "Tab") {
      trapFocusInOverlay(event);
      return;
    }
    if (event.key !== "Escape") return;
    if (appHasOpenPanel()) return;
    event.preventDefault();
    closeOverlay();
  };

  const isolatePageForOverlay = () => {
    if (!overlay) return;
    restorePageFromOverlay();
    isolatedPageChildren = [];
    Array.from(document.body.children).forEach((child) => {
      if (
        child === overlay
        || ["SCRIPT", "STYLE", "LINK", "META", "NOSCRIPT"].includes(child.tagName)
        || isCartLayerElement(child)
      ) {
        return;
      }
      isolatedPageChildren.push({
        element: child,
        ariaHidden: child.getAttribute("aria-hidden"),
        inert: "inert" in child ? child.inert : undefined
      });
      child.setAttribute("aria-hidden", "true");
      if ("inert" in child) child.inert = true;
    });
  };

  const restorePageFromOverlay = () => {
    isolatedPageChildren.forEach(({ element, ariaHidden, inert }) => {
      if (!element || !element.isConnected) return;
      if (ariaHidden === null) element.removeAttribute("aria-hidden");
      else element.setAttribute("aria-hidden", ariaHidden);
      if ("inert" in element && inert !== undefined) element.inert = inert;
    });
    isolatedPageChildren = [];
  };

  const buildAppUrl = (sourceUrl) => {
    const source = sameOriginUrl(sourceUrl || window.location.href);
    const sourceParams = source.searchParams;
    const app = new URL(APP_PATH, window.location.origin);

    app.searchParams.set("portal", "1");
    app.searchParams.set("swipe_launch_path", `${source.pathname}${source.search}`);
    app.searchParams.set("swipe_launch_source", productHandleFromPath(source) ? "product_page" : "storefront");

    const product = firstParam(sourceParams, ["swipe_product", "product"]) || productHandleFromPath(source);
    appendIfPresent(app.searchParams, "swipe_product", product);
    appendIfPresent(app.searchParams, "swipe_view", firstParam(sourceParams, ["swipe_view", "view"]));
    appendIfPresent(app.searchParams, "swipe_filter", firstParam(sourceParams, ["swipe_filter", "filter"]));
    appendIfPresent(app.searchParams, "swipe_country", firstParam(sourceParams, ["swipe_country", "country"]));
    appendIfPresent(app.searchParams, "swipe_mission", firstParam(sourceParams, ["swipe_mission", "mission"]));
    appendIfPresent(app.searchParams, "swipe_type", firstParam(sourceParams, ["swipe_type", "swipe_snack_type", "type"]));
    appendIfPresent(app.searchParams, "swipe_intro", firstParam(sourceParams, ["swipe_intro", "showIntro"]));
    appendIfPresent(app.searchParams, "swipe_debug", firstParam(sourceParams, ["swipe_debug"]));

    sourceParams.forEach((value, key) => {
      if (/^(utm_|gclid$|fbclid$|msclkid$)/i.test(key)) {
        app.searchParams.set(key, value);
      }
    });

    return app.toString();
  };

  const buildCandidateAppUrls = (sourceUrl) => {
    const source = sameOriginUrl(sourceUrl || window.location.href);
    const sourceParams = source.searchParams;
    const product = firstParam(sourceParams, ["swipe_product", "product"]) || productHandleFromPath(source);
    const candidates = [];

    APP_PATHS.forEach((path) => {
      const app = new URL(path, window.location.origin);
      app.searchParams.set("portal", "1");
      app.searchParams.set("swipe_launch_path", `${source.pathname}${source.search}`);
      app.searchParams.set("swipe_launch_source", productHandleFromPath(source) ? "product_page" : "storefront");

      appendIfPresent(app.searchParams, "swipe_product", product);
      appendIfPresent(app.searchParams, "swipe_view", firstParam(sourceParams, ["swipe_view", "view"]));
      appendIfPresent(app.searchParams, "swipe_filter", firstParam(sourceParams, ["swipe_filter", "filter"]));
      appendIfPresent(app.searchParams, "swipe_country", firstParam(sourceParams, ["swipe_country", "country"]));
      appendIfPresent(app.searchParams, "swipe_mission", firstParam(sourceParams, ["swipe_mission", "mission"]));
      appendIfPresent(app.searchParams, "swipe_type", firstParam(sourceParams, ["swipe_type", "swipe_snack_type", "type"]));
      appendIfPresent(app.searchParams, "swipe_intro", firstParam(sourceParams, ["swipe_intro", "showIntro"]));
      appendIfPresent(app.searchParams, "swipe_debug", firstParam(sourceParams, ["swipe_debug"]));

      sourceParams.forEach((value, key) => {
        if (/^(utm_|gclid$|fbclid$|msclkid$)/i.test(key)) {
          app.searchParams.set(key, value);
        }
      });

      candidates.push(app.toString());
    });

    return candidates;
  };

  const launchStateFor = (sourceUrl) => {
    const app = new URL(buildAppUrl(sourceUrl));
    return {
      product: app.searchParams.get("swipe_product") || "",
      view: app.searchParams.get("swipe_view") || "",
      intro: app.searchParams.get("swipe_intro") || "",
      debug: app.searchParams.get("swipe_debug") || ""
    };
  };

  const ensureOverlay = () => {
    if (overlay) return overlay;

    overlay = document.createElement("div");
    overlay.id = LAUNCHER_ID;
    overlay.className = "gs-swipe-mode-overlay";
    overlay.setAttribute("aria-hidden", "true");

    overlay.innerHTML = [
      '<div class="gs-swipe-mode-overlay__frame" role="dialog" aria-modal="true" aria-label="Galactic Snacks Swipe Mode">',
      '  <div class="gs-swipe-mode-overlay__mount" data-gs-swipe-mode-mount></div>',
      '</div>',
      '<button class="gs-swipe-mode-overlay__close" type="button" aria-label="Close Swipe Mode"></button>',
      '<div class="gs-swipe-mode-overlay__transition" aria-hidden="true">',
      '  <div class="gs-swipe-mode-overlay__brand">',
      LOGO_ASSET ? `    <img src="${LOGO_ASSET}" alt="">` : "",
      '    <span data-gs-swipe-transition-label>Entering Swipe Mode</span>',
      '  </div>',
      '</div>'
    ].join("");

    document.body.appendChild(overlay);
    mount = overlay.querySelector("[data-gs-swipe-mode-mount]");
    overlay.querySelector("button")?.addEventListener("click", () => closeOverlay());

    return overlay;
  };

  const setPageLock = (locked) => {
    if (locked) {
      previousBodyOverflow = document.body.style.overflow;
      previousDocumentOverflow = document.documentElement.style.overflow;
      document.body.classList.add("gs-swipe-mode-lock");
      document.documentElement.classList.add("gs-swipe-mode-lock");
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
      isolatePageForOverlay();
      return;
    }
    restorePageFromOverlay();
    document.body.classList.remove("gs-swipe-mode-lock");
    document.documentElement.classList.remove("gs-swipe-mode-lock");
    document.body.style.overflow = previousBodyOverflow;
    document.documentElement.style.overflow = previousDocumentOverflow;
  };

  const cleanSwipeParamsFromUrl = () => {
    const url = new URL(window.location.href);
    SWIPE_CONTROL_PARAMS.forEach((param) => url.searchParams.delete(param));
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(window.history.state, "", next || url.pathname);
  };

  const ensureSwipeParamInUrl = (sourceUrl) => {
    const url = sameOriginUrl(sourceUrl || window.location.href);
    if (url.origin !== window.location.origin) return;
    url.searchParams.set("swipe", "1");
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  };

  const refreshCartDrawer = (payload = {}) => {
    debugLog("cart refresh requested", payload);
    document.dispatchEvent(new CustomEvent("cart:refresh", { detail: payload }));
    document.dispatchEvent(new CustomEvent("rebuy:cart.change", { detail: payload }));
    if (window.Rebuy && window.Rebuy.SmartCart && typeof window.Rebuy.SmartCart.show === "function") {
      debugLog("opening Rebuy SmartCart", payload);
      window.Rebuy.SmartCart.show();
    }
  };

  const destroyMountedApp = () => {
    if (window.GalacticSnackSwipeApp && typeof window.GalacticSnackSwipeApp.destroyAll === "function") {
      window.GalacticSnackSwipeApp.destroyAll();
      return;
    }
    mount?.querySelectorAll(".gs-swipe[data-initialized]").forEach((root) => {
      if (typeof root.__gsSwipeDestroy === "function") root.__gsSwipeDestroy();
    });
  };

  const relayAnalyticsEvent = (payload = {}) => {
    const eventName = typeof payload.event_name === "string" ? payload.event_name : "";
    const params = payload.params && typeof payload.params === "object" ? payload.params : {};
    if (!eventName || !eventName.startsWith("snack_swipe_")) return;
    debugLog("analytics relay", { eventName, params });
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ...params
    });

  };

  const escapeHtml = (value) => String(value || "").replace(/[&<>"']/g, (char) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;"
  })[char]);

  const findAppRoot = (doc) => (
    doc.querySelector("[data-gs-swipe]") ||
    doc.querySelector(".gs-swipe") ||
    doc.querySelector("[data-gs-products]")?.closest("section")
  );

  const failureMessageFor = (doc, url) => {
    const title = doc.querySelector("title")?.textContent?.trim();
    const bodyText = doc.body?.textContent?.replace(/\s+/g, " ").trim().slice(0, 140);
    const path = new URL(url).pathname;
    if (title || bodyText) {
      return `${path} did not include Swipe app markup. Returned: ${title || bodyText}`;
    }
    return `${path} did not include Swipe app markup.`;
  };

  const loadSwipeApp = async (sourceUrl) => {
    if (!mount) return;
    destroyMountedApp();
    mount.innerHTML = '<div class="gs-swipe-mode-overlay__loading">Loading Swipe Mode</div>';

    let appRoot = null;
    let lastFailure = "";
    const candidateUrls = buildCandidateAppUrls(sourceUrl);

    for (const appUrl of candidateUrls) {
      try {
        debugLog("fetching app source", { appUrl });
        const response = await fetch(appUrl, {
          credentials: "same-origin",
          cache: "no-store",
          headers: {
            "Accept": "text/html"
          }
        });
        if (!response.ok) {
          lastFailure = `${new URL(appUrl).pathname} returned ${response.status}`;
          continue;
        }

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        appRoot = findAppRoot(doc);
        if (appRoot) {
          debugLog("found app root", { path: new URL(appUrl).pathname });
          break;
        }
        lastFailure = failureMessageFor(doc, appUrl);
      } catch (error) {
        lastFailure = error?.message || "Swipe app request failed.";
      }
    }

    if (!appRoot) throw new Error(lastFailure || "Swipe app markup was not found.");

    const launchState = launchStateFor(sourceUrl);
    appRoot.removeAttribute("data-initialized");
    appRoot.dataset.gsPortalMode = "true";
    if (launchState.product) appRoot.dataset.swipeProduct = launchState.product;
    if (launchState.view) appRoot.dataset.swipeView = launchState.view;
    if (launchState.intro) appRoot.dataset.swipeIntro = launchState.intro;
    if (launchState.debug) appRoot.dataset.swipeDebug = launchState.debug;
    mount.replaceChildren(document.importNode(appRoot, true));

    if (!window.GalacticSnackSwipeApp || typeof window.GalacticSnackSwipeApp.init !== "function") {
      throw new Error("Swipe app script was not available on this page.");
    }

    window.GalacticSnackSwipeApp.init();
    debugLog("app initialized", launchState);

    overlay?.classList.add("is-ready");
    overlay?.classList.remove("is-error");
  };

  const openOverlay = async (sourceUrl, options = {}) => {
    activeDebug = debugEnabledFor(sourceUrl);
    if (!canLaunchSwipeMode(sourceUrl)) {
      debugLog("desktop launch blocked", { sourceUrl });
      if (options.cleanUrl !== false) cleanSwipeParamsFromUrl();
      return;
    }

    ensureOverlay();
    if (!mount) return;

    lastActiveElement = document.activeElement;
    isOpen = true;
    debugLog("opening overlay", { sourceUrl });
    setPageLock(true);
    if (options.updateUrl !== false) ensureSwipeParamInUrl(sourceUrl);

    overlay.setAttribute("aria-hidden", "false");
    overlay.classList.remove("is-error", "is-exiting", "is-ready");
    overlay.classList.add("is-open", "is-entering");
    const label = overlay.querySelector("[data-gs-swipe-transition-label]");
    if (label) label.textContent = "Entering Swipe Mode";

    try {
      await loadSwipeApp(sourceUrl);
    } catch (error) {
      debugLog("app load failed", { message: error?.message });
      console.warn("Snack Swipe overlay could not load", error);
      if (mount) {
        mount.innerHTML = `<div class="gs-swipe-mode-overlay__error"><strong>Swipe Mode could not load.</strong><span>${escapeHtml(error?.message || "Please try refreshing the page.")}</span></div>`;
      }
      overlay.classList.add("is-error", "is-ready");
    }

    window.setTimeout(focusInitialOverlayTarget, 0);
    window.setTimeout(() => {
      overlay?.classList.remove("is-entering");
    }, 820);
  };

  const closeOverlay = () => {
    if (!overlay || !isOpen) return;
    isOpen = false;
    const label = overlay.querySelector("[data-gs-swipe-transition-label]");
    if (label) label.textContent = "Exiting Swipe Mode";
    overlay.classList.remove("is-entering");
    overlay.classList.add("is-exiting");
    window.dispatchEvent(new CustomEvent(`${MESSAGE_PREFIX}close-requested`));

    window.setTimeout(() => {
      if (!overlay) return;
      overlay.classList.remove("is-open", "is-ready", "is-exiting");
      overlay.setAttribute("aria-hidden", "true");
      destroyMountedApp();
      if (mount) mount.innerHTML = "";
      setPageLock(false);
      cleanSwipeParamsFromUrl();
      debugLog("closed overlay");
      if (lastActiveElement && typeof lastActiveElement.focus === "function") {
        lastActiveElement.focus({ preventScroll: true });
      }
    }, 560);
  };

  const bindLaunchers = () => {
    document.addEventListener("click", (event) => {
      const trigger = event.target.closest(LAUNCH_TRIGGER_SELECTOR);
      if (!trigger) return;
      const href = trigger.getAttribute("href") || window.location.href;
      const url = sameOriginUrl(href);
      if (url.origin !== window.location.origin) return;
      if (trigger.matches("a") && url.searchParams.get("swipe") !== "1" && !trigger.hasAttribute("data-gs-open-swipe-mode")) return;
      event.preventDefault();
      openOverlay(url.toString(), { updateUrl: true });
    });
  };

  const bindAppEvents = () => {
    window.addEventListener(`${MESSAGE_PREFIX}ready`, () => {
      overlay?.classList.add("is-ready");
    });
    window.addEventListener(`${MESSAGE_PREFIX}exit`, () => {
      closeOverlay();
    });
    window.addEventListener(`${MESSAGE_PREFIX}cart-updated`, (event) => {
      refreshCartDrawer(event.detail || {});
    });
    window.addEventListener(`${MESSAGE_PREFIX}analytics`, (event) => {
      relayAnalyticsEvent(event.detail || {});
    });
    window.addEventListener("message", (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data || {};
      if (data.type !== `${MESSAGE_PREFIX}analytics`) return;
      relayAnalyticsEvent(data);
    });
  };

  const init = () => {
    bindAppEvents();
    bindLaunchers();
    document.addEventListener("keydown", handleOverlayKeydown);
    watchEntryImpressions();
    const current = new URL(window.location.href);
    if (current.searchParams.get("swipe") === "1") {
      openOverlay(current.toString(), { updateUrl: false });
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
